import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface PermissionGateProps {
  /** A single `<module>.<action>` key. */
  permission?: string;
  /** Satisfied when the caller holds any one of these. */
  anyOf?: string[];
  children: ReactNode;
  /**
   * Rendered instead of the children when the caller lacks the permission. Leave unset to
   * render nothing — hiding is the default for a pure permission gate, because showing a
   * disabled control the user can never enable is noise, not information.
   */
  fallback?: ReactNode;
}

/**
 * Wraps any nav entry, route, or action the caller may not be allowed to use.
 *
 * The backend resolves implicit-read inheritance before it sends the permission set, so
 * gating on `<module>.read` here is enough — visibility falls out automatically.
 */
export function PermissionGate({ permission, anyOf, children, fallback = null }: PermissionGateProps) {
  const { hasPermission, hasAnyPermission } = useAuth();

  const allowed = permission
    ? hasPermission(permission)
    : anyOf
      ? hasAnyPermission(anyOf)
      : true;

  return <>{allowed ? children : fallback}</>;
}
